import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { dataToColors, getChartThemeColors } from "../../../util/colors";
import { useSeries } from "../../../hooks/Data";
import { Card, Table, CardMinzd, SimpleCrossChart } from "../../../components";
import { removerCaracteresEspeciais } from "../../../util/formatters/stringNormalizer";
import {
  fetchCrossConfigResult,
  deleteSimpleCrossConfig,
} from "../../../stores/actions/simpleCross";
import { fetchSimpleCrossChartColorConfigData } from "../../../stores/actions/charts";

import { tiposFiltroTabela } from "../../../components/Table/tableFilterHandler";
import { cardsMap } from "../cardsMap";
import { graphTypes } from "../types";
import * as Ui from "../styles";
import SimpleCrossConfig from "./config/SimpleCrossConfig";
import ContextualCardAnnotations from "../../ContextualCardAnnotations";
import { annotationStrategyMap } from "../../../stores/actions/annotations";
import { formatSimpleCrossData } from "./formatData";
import { debounce } from "../../../util/fnc/debounce";
import ContextualPopupExporting from "../../ContextualPopupExporting";
import { exportingTypes } from "../exportingTypes";
import { getQuestionTitlesByChartType } from "../../../components/Card/CardExporting/chartExporter/questionProcessor";
import { SIMPLECROSS_CARD } from "../cardTypes";
import ConfigurableChartThemeManager from "src/managers/themeManager/inheritorThemeManager/ConfigurableChartThemeManager";
import { useEventBus, usePromptConfirmEvolutive } from "src/hooks";
import {
  ON_CREATE_CONSOLIDATIONS,
  ON_DELETE_CONSOLIDATIONS,
  ON_EDIT_CONSOLIDATIONS,
  ON_OPEN_SIMPLE_CROSS_CONFIG,
  ON_SIMPLECROSS_CONFIG_CHANGE,
} from "src/hooks/EventBus/eventTypes";
import { usePreviousValue } from "src/hooks/Data";
import useFlashMessage from "src/hooks/FlashMessage";
import { resetChartLegendsState } from "src/managers/chartManager/stateManagement/legendHandler";

/**
 * filters a dataset using the data stored in the given
 * dataField that matches with the given keywords.
 */
// {dataset, datasetFilterField, query}
function filterByKeywords(context) {
  const { dataset, datasetFilterField, query } = context;
  if (!query) {
    return dataset;
  }
  if (query && query === "") {
    return dataset;
  }
  return dataset.filter((data) =>
    removerCaracteresEspeciais(data[datasetFilterField])
      .toLowerCase()
      .includes(removerCaracteresEspeciais(query).toLowerCase()),
  );
}

/**
 * @param data dados do grafico simples
 * @param isFullScreen flag indicando se o card do gráfico simples deve ser exibido
 * em fullScreen ou não.
 * @param isSomeoneInFullScreen flag indicando se existe algum card em full screen.
 * @param onFullScreenToggle callback chamado quando existe uma interação com o botão toggle de fullscreen.
 */
function SimpleCrossChartCard(props) {
  // title this card
  const title = "Cruzamentos Simples";
  // props from redux
  const { dispatch, resultRunning, resultError, resultPayload } = props;
  // props from parent
  const {
    customerId,
    surveyId,
    questionId,
    questionTitle,
    isFullScreen,
    isSomeoneInFullScreen,
    onFullScreenToggle,
  } = props;

  // event bus
  const eventBus = useEventBus();

  //
  const previousRunningState = usePreviousValue(resultRunning);

  // prompt
  const [
    openPromptConfirmEvolutive,
    renderPromptConfirmEvolutive,
  ] = usePromptConfirmEvolutive();

  //responsável pelas flash message
  const [handleFlashMessage] = useFlashMessage();

  // props from theme
  const { theme } = props;
  // refes
  const cardRef = useRef();
  const chartRef = React.createRef();

  // ChartRef aqui não segue o padrão dos outros cards, por quê?
  // O gráfico de cruzamentos simples pode renderizar um ou mais charts
  // cada posição no array de referência é uma instância de echarts.
  chartRef.current = [];

  // filtro inicial
  const initialFilter = {
    type: tiposFiltroTabela.filtrarPergunta,
    keywords: "",
  };
  // state
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const [simpleCrossChartType, setSimpleCrossChartType] = useState();
  const [showConfig, setShowConfig] = useState(false);
  const [isMinimizedScreen, setMinimizeScreen] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [showExporting, setShowExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [payloadData, setPayloadData] = useState({});
  const [onDeleteRequest, setOnDeleteRequest] = useState(false);
  // Seleciona os tipos de gráfico que serão exibidos de acordo com a resolução
  const [chartTypes, setChartTypes] = useState([]);

  // Gerenciador de tema de cores e fontes
  const themeManager = new ConfigurableChartThemeManager(
    questionId,
    surveyId,
    !!data,
    chartRef,
    theme,
    simpleCrossChartType,
    SIMPLECROSS_CARD,
  );

  const {
    configurationError: simpleCrossError,
    fontsSizes,
    colorSchemeValue,
    colorPickerColors,
  } = themeManager.getConfig();

  // use callback
  const carregarResultados = useCallback(() => {
    fetchCrossConfigResult(customerId, surveyId, questionId, dispatch);
  }, [customerId, surveyId, questionId, dispatch]);

  // handle full screen toggle
  function handleFullScreenToggle(cardId, isFull) {
    if (onFullScreenToggle) onFullScreenToggle(cardId, isFull);
  }

  /**
   * Resolve quais os tipos de visualizacao
   * disponiveis
   */
  function resolveChartTypes() {
    const windowWidth = window.innerWidth;

    if (windowWidth < 586) {
      return graphTypes.get("cruzamento-simples-mobile").type;
    }
    return graphTypes.get("cruzamento-simples").type;
  }

  /**
   * Subscribers
   */
  useEffect(() => {
    eventBus.subscribe(ON_OPEN_SIMPLE_CROSS_CONFIG, () => {
      setShowConfig(true);
    });
  }, []);

  /**
   * Trata qualquer mudança nas consolidações que impactam o gráfico
   * de cruzamento simples.
   */
  const handleConsolidationChange = useCallback(() => {
    if (!surveyId || !questionId) return;

    setData(null);
    resetChartLegendsState(SIMPLECROSS_CARD);
    carregarResultados();
  }, [surveyId, questionId, carregarResultados]);

  /**
   * Inscreve o card nos eventos de criação, edição e exclusão de consolidações,
   * garantindo que o gráfico seja atualizado automaticamente quando houver mudanças.
   */
  useEffect(() => {
    const unsubCreate = eventBus.subscribe(
      ON_CREATE_CONSOLIDATIONS,
      handleConsolidationChange,
      false,
    );

    const unsubDelete = eventBus.subscribe(
      ON_DELETE_CONSOLIDATIONS,
      handleConsolidationChange,
      false,
    );

    const unsubEdit = eventBus.subscribe(
      ON_EDIT_CONSOLIDATIONS,
      handleConsolidationChange,
      false,
    );

    return () => {
      unsubCreate && unsubCreate();
      unsubDelete && unsubDelete();
      unsubEdit && unsubEdit();
    };
  }, [eventBus, handleConsolidationChange]);

  /**
   * Effects: Verifica se existem dados plotados no gráfico quando o usuário
   * selecionar um cliente, pesquisa e pergunta no dashboard.
   *
   * Caso existam dados, será notificado um evento "ON_SIMPLECROSS_CONFIG_CHANGE"
   * que será recebido no card do gráfico evolutivo de cruzamentos.
   *
   * Por quê? Dada a regra de negócio definida, o card do gráfico de evolutivo
   * de cruzamentos só pode ser configurável caso exista configuração de cruzamento simples
   * para o cliente / pesquisa / pergunta atual.
   */
  useEffect(() => {
    const isFullyLoaded =
      !resultRunning && !!previousRunningState !== !!resultRunning;
    if (isFullyLoaded && !!Object.keys(resultPayload).length) {
      eventBus.notify(ON_SIMPLECROSS_CONFIG_CHANGE, true);
    } else if (isFullyLoaded) {
      eventBus.notify(ON_SIMPLECROSS_CONFIG_CHANGE, false);
    }
  }, [previousRunningState, resultRunning, resultPayload]);

  /**
   * Atualiza o chartType com os tipos disponiveis
   * no momento
   */
  const updateChartType = useCallback(() => {
    setChartTypes(resolveChartTypes());
  }, []);

  // Listener para redimencionamento de tela, para definir quais gráficos exibir
  window.addEventListener(
    "resize",
    debounce(() => {
      updateChartType();
    }, 1000),
  );

  /**
   * De acordo com a disponibilidade de tipos de charts,
   * podemos ter uma visualizacao em tabela permitida e
   * 'default' ou nao.
   */
  const resolveChartTypeResponsivity = useCallback(() => {
    const canShowTableMode =
      chartTypes.find((chartType) => chartType.value === "table") && true;
    if (canShowTableMode && !simpleCrossChartType) {
      setSimpleCrossChartType("table");
    } else if (!canShowTableMode) {
      setSimpleCrossChartType("bar");
    }
  }, [chartTypes, simpleCrossChartType]);

  /**
   * Callbacks executados quando ha alteracao em chartTypes
   */
  useEffect(() => {
    // na inicializacao, chartTypes esta vazio, inicializando valores...
    if (!chartTypes || chartTypes.length === 0) {
      updateChartType();
    } else {
      // com os valores de chartTypes carregados...
      resolveChartTypeResponsivity();
    }
  }, [chartTypes, updateChartType, resolveChartTypeResponsivity]);

  useEffect(() => {
    setIsLoading(resultRunning);
  }, [resultRunning]);

  /**
   * Gancho: O usuário deleta uma configuração
   * Efeito: Limpa os dados plotados da configuração e dispara uma mensagem indicadora.
   */
  useEffect(() => {
    if (onDeleteRequest && !resultError) {
      onDeleteConfig();
    }
  }, [onDeleteRequest, resultError]);

  // quando ocorre erro
  useEffect(() => {
    if (resultError) {
      if (resultError.status === 404) {
        setData(null);
        resetChartLegendsState(SIMPLECROSS_CARD);
        if (cardRef.current) cardRef.current.clearTypedSearch();
      }
    }
  }, [resultError]);

  // quando ocorre erro no carregamento das configuracoes do grafico de cruzamento simples
  useEffect(() => {
    if (simpleCrossError) {
      toast.error(simpleCrossError.toString());
    }
  }, [simpleCrossError]);

  /**
   * Callback para quando a pergunta principal eh modificada
   */
  useEffect(() => {
    // clear datas
    setData(null);
    resetChartLegendsState(SIMPLECROSS_CARD);
    // clearTypedSearch
    if (cardRef.current) cardRef.current.clearTypedSearch();
    // does nothing if question not exists
    if (questionId) {
      // load new results
      carregarResultados(surveyId, questionId);
    }
  }, [surveyId, questionId, carregarResultados]);

  /**
   * Callback para alteracoes de resultados
   * de cruzamentos
   */
  useEffect(() => {
    const newData = resultPayload[`${surveyId}_${questionId}`];

    setPayloadData(newData);
    if (newData) {
      resetChartLegendsState(SIMPLECROSS_CARD);
      setData(newData);
      fetchSimpleCrossChartColorConfigData(surveyId, questionId, dispatch);
    }
  }, [surveyId, questionId, resultPayload, dispatch]);

  /**
   * manipula a solicitação de apresentar o modal de configuração
   */
  function handleOpenConfig() {
    setShowConfig(true);
  }

  function handleCloseConfig() {
    setShowConfig(false);
  }

  function handleCloseAnnotations() {
    setShowAnnotations(false);
  }

  function handleAnnotationsCancel() {
    handleCloseAnnotations();
  }

  function handleCloseExporting() {
    setShowExporting(false);
  }

  function handleExportingCancel() {
    handleCloseExporting();
  }

  function handleOpenAnnotations() {
    setShowAnnotations(true);
  }

  function handleOpenExporting() {
    setShowExporting(true);
  }

  function handleAnnotationsSave() {
    setShowAnnotations(false);
  }

  function onDeleteConfig() {
    // limpa os dados plotados
    setData(null);
    // reseta o estado das legendas compartilhadas
    resetChartLegendsState(SIMPLECROSS_CARD);
    // mostra a flash message de remoção
    handleFlashMessage(
      "Cruzamento excluído",
      "O cruzamento foi excluído com sucesso",
      "success",
    );
    // reseta o estado que indica se foi requerido a ação de delete da configuração.
    setOnDeleteRequest(false);
  }

  function handleDeleteOption() {
    openPromptConfirmEvolutive(() => {
      setOnDeleteRequest(true);
      deleteSimpleCrossConfig(surveyId, customerId, questionId, dispatch);
    });
  }

  function handleAnnotationsFailure(errorType) {
    const errorStrategy = {
      fetch: () => {
        handleCloseAnnotations();
        const message =
          "Houve uma falha ao recuperar sua anotação, tente novamente";
        toast.error(message);
      },
      save: () => {
        const message = "Não foi possível salvar sua anotação, tente novamente";
        toast.error(message);
      },
    };
    errorStrategy[errorType]();
  }

  /**
   * manipula a solicitação de salvar a configuração
   */
  function handleSaveConfig() {
    handleCloseConfig();
    carregarResultados(surveyId, questionId);
  }

  /**
   * manipula a solicitação de minimizar o card
   */
  function handleMinimize() {
    setMinimizeScreen(true);
  }

  /**
   * manipula a alteração do filtro de legendas
   */
  function handleChangeFilterValue({ type, keywords }) {
    setFilter({ type, keywords });
  }

  // manipula a alteração do tipo de gráfico ou da tabela
  function handleTypeChange(newType) {
    setSimpleCrossChartType(newType);
  }

  // handle color scheme type change
  function handleColorSchemeChange(newType) {
    themeManager.updateColorScheme(newType);
  }

  // handle chart font type change
  function handleChartFontChange(newFontsSizes) {
    themeManager.updateFontSize(newFontsSizes);
  }

  // for render configurations
  // menu-structure: {type: [<charttypes>|<configbtn>]}
  // menu-structure: {charttypes = {type, key, selectedIndex, onSelect, data[{value, label}]}}
  // menu-structure: {configbtn = {type, key, onclick}}
  function buildMenu(data) {
    // result
    const result = [];
    // create chart menu
    const selectedTypeIndex = chartTypes.findIndex(
      (type) => type.value === simpleCrossChartType,
    );

    const colorSchemeTypes = graphTypes.get("cruzamento-simples")
      .colorSchemeType;
    const selectedColorSchemeTypeIndex = colorSchemeTypes.findIndex(
      (type) => type.value === colorSchemeValue,
    );

    const chartFontTypes = graphTypes.get("cruzamento-simples").chartFontsType;
    const selectedFontTypeIndex = chartFontTypes.findIndex(
      (type) => type.value === fontsSizes,
    );

    const chartTypesOption = {
      type: "charttypes",
      selectedIndex: selectedTypeIndex,
      data: chartTypes,
      onSelect: handleTypeChange,
    };

    const colorSchemeOption = {
      type: "colorscheme",
      selectedIndex: selectedColorSchemeTypeIndex,
      data: colorSchemeTypes,
      onSelect: handleColorSchemeChange,
    };

    const chartFontsOption = {
      type: "chartfonts",
      selectedIndex: selectedFontTypeIndex,
      data: chartFontTypes,
      onSelect: handleChartFontChange,
    };

    const annotationsOption = {
      type: "annotations",
      onClick: handleOpenAnnotations,
    };

    const exportChartOption = {
      type: "exportChart",
      onClick: handleOpenExporting,
    };

    const deleteOption = {
      type: "deleteItem",
      title: "Excluir cruzamento",
      hoverTitle: "Excluir cruzamento",
      onClick: handleDeleteOption,
    };

    const configurationOption = {
      type: "configbtn",
      onClick: handleOpenConfig,
      title: "Configurar cruzamento...",
    };

    const options = data
      ? [
          chartTypesOption,
          annotationsOption,
          exportChartOption,
          deleteOption,
          configurationOption,
        ]
      : [
          chartTypesOption,
          colorSchemeOption,
          chartFontsOption,
          configurationOption,
        ];
    if (data && simpleCrossChartType !== "table") {
      options.splice(2, 0, colorSchemeOption, chartFontsOption);
    }
    result.push(
      ...options.map((option, index) => ({ ...option, key: `#key${index}` })),
    );

    return result;
  }

  function renderContent(type) {
    if (type === "table") {
      return (
        <Table
          customerId={customerId}
          surveyId={surveyId}
          data={formatSimpleCrossData(data)}
          filtering={filter}
          isFullScreen={isFullScreen}
        />
      );
    }

    return (
      <SimpleCrossChart
        chartFontSize={fontsSizes}
        type={type}
        data={formatSimpleCrossData(data)}
        colors={themeManager.calculatedChartColorArray}
        colorPickerColors={colorPickerColors}
        updateSimpleCrossChartColors={themeManager.updateChartColors}
        legendsFilter={filter}
        legendsFilterHandler={filterByKeywords}
        theme={theme.colors.charts}
        themeTitle={theme.title}
        error={simpleCrossError}
        chartRef={chartRef}
        chartType={simpleCrossChartType}
      />
    );
  }

  function showCardType(type) {
    if (type === "table" && data) {
      return "MODO TABELA";
    }
    if (type === "bar" && data) {
      return "MODO GRÁFICO";
    }
    return "";
  }

  function renderChartCard() {
    return isMinimizedScreen ? (
      <CardMinzd title={title} onRestore={() => setMinimizeScreen(false)} />
    ) : (
      <Card
        minimizable
        ref={cardRef}
        cardId={cardsMap.cruzamentoSimples.cardId}
        cardTitle={title}
        cardTooltip={
          "Visualize o cruzamento de todas as alternativas de duas ou mais perguntas selecionadas"
        }
        questionTitle={questionTitle}
        filtersTypes={[
          {
            label: "Filtrar por perguntas",
            value: tiposFiltroTabela.filtrarPergunta,
          },
          {
            label: "Filtrar por respostas",
            value: tiposFiltroTabela.filtrarAlternativa,
          },
        ]}
        isFullScreenMode={isFullScreen}
        onFullScreenModeChange={handleFullScreenToggle}
        typedSearchPlaceholder={"Aplicar filtro"}
        onTypeChange={handleTypeChange}
        menu={buildMenu(payloadData)}
        onChangeFilterValue={handleChangeFilterValue}
        onMaximize={handleFullScreenToggle}
        onMinimize={handleMinimize}
        withData={!!data}
        loading={isLoading}
        theme={theme}
        cardType={showCardType(simpleCrossChartType)}
      >
        {renderContent(simpleCrossChartType)}
      </Card>
    );
  }

  function renderConfig() {
    return showConfig ? (
      <SimpleCrossConfig
        customerId={customerId}
        surveyId={surveyId}
        questionId={questionId}
        questionTitle={questionTitle}
        isOpen={showConfig}
        onCancel={handleCloseConfig}
        onSave={handleSaveConfig}
        initialData={data}
      />
    ) : null;
  }

  function renderAnnotations() {
    return showAnnotations ? (
      <ContextualCardAnnotations
        strategy={annotationStrategyMap.simpleCross}
        customerId={customerId}
        surveyId={surveyId}
        resultKey={questionId}
        onCancel={handleAnnotationsCancel}
        onSave={handleAnnotationsSave}
        onFailure={handleAnnotationsFailure}
      />
    ) : null;
  }

  function renderExporting() {
    return showExporting ? (
      <ContextualPopupExporting
        customerId={customerId}
        surveyId={surveyId}
        analyzeId={questionId}
        onCancel={handleExportingCancel}
        types={
          simpleCrossChartType === "table"
            ? exportingTypes.slice(1, 3)
            : exportingTypes
        }
        theme={theme}
        selectedChartType={simpleCrossChartType}
        selectedCardType={SIMPLECROSS_CARD}
        questionLabels={getQuestionTitlesByChartType(SIMPLECROSS_CARD, data)}
      />
    ) : null;
  }

  return (
    <Ui.CardContainer
      className="cardContainer"
      cardIsFullScreen={isFullScreen}
      someoneInFullScreen={isSomeoneInFullScreen}
    >
      {/* chart cart */}
      {renderChartCard()}
      {/* modal */}
      {renderConfig()}
      {renderAnnotations()}
      {renderExporting()}
      {/* prompt */}
      {renderPromptConfirmEvolutive(
        "Excluir cruzamento?",
        "Ao excluir o cruzamento você perderá as configurações.",
      )}
    </Ui.CardContainer>
  );
}

const mapStateToProps = (state) => {
  const { result: resultState } = state.simpleCross;

  return {
    resultRunning: resultState.running,
    resultError: resultState.error,
    resultPayload: resultState.payload,
  };
};

export default connect(mapStateToProps)(SimpleCrossChartCard);
